import {
  EAutocompleteCategoryDefinitionProps,
  EAutocompleteOption,
} from 'webapp-lib/Components';

import contactsHandledBackend, {
  ContactEmailAddressWithInvolvements,
  ContactWithInvolvements,
  InvolvementWithLabeledRoles,
} from '@lexian/api/contacts-api';
import i18n from '_/i18n';
import {getContactTypeIcon} from '_/utilities/contacts.utils';
import {handlePaginatedLoadableFetch} from '_/utilities/simple-fetch';
import {
  PageItem,
  PaginatedLoadable,
  PaginationArguments,
} from '_/utilities/types';

import {PaginatedSearchContactBackendCall} from './types';

export const buildSearchContactEmailAddressesWithInvolvments =
  (casusId: string, excludeIds?: string[]) =>
  (
    paginationArguments: Omit<PaginationArguments, 'cursor'>,
    currentLoadable: PaginatedLoadable<
      PageItem<ContactEmailAddressWithInvolvements>[]
    >,
    {searchQuery}: {searchQuery: string}
  ): Promise<
    PaginatedLoadable<PageItem<ContactEmailAddressWithInvolvements>[]>
  > =>
    handlePaginatedLoadableFetch(
      {
        endpointFunction:
          contactsHandledBackend.searchContactEmailAddressesWithInvolvements,
        otherParams: {query: searchQuery, casusId, excludeIds},
        getPageItemFromResponse: (body) => body.contacts,
      },
      paginationArguments,
      currentLoadable
    );

export const buildSearchContacts =
  <T extends object, EXTRA_PARAMS>(
    backendCall: PaginatedSearchContactBackendCall<T, EXTRA_PARAMS>,
    extraParams?: EXTRA_PARAMS
  ) =>
  (
    paginationArguments: Omit<PaginationArguments, 'cursor'>,
    currentLoadable: PaginatedLoadable<PageItem<T>[]>,
    {searchQuery}: {searchQuery: string}
  ): Promise<PaginatedLoadable<PageItem<T>[]>> => {
    const otherParams = {
      query: searchQuery,
      ...(extraParams ?? ({} as EXTRA_PARAMS)),
    };

    return handlePaginatedLoadableFetch(
      {
        endpointFunction: backendCall,
        otherParams,
        getPageItemFromResponse: (body) => body.contacts,
      },
      paginationArguments,
      currentLoadable
    );
  };

export const disableSelectedOptions = (
  options: EAutocompleteOption[],
  selectedValues: string[]
): EAutocompleteOption[] =>
  options.map((option) => ({
    ...option,
    disabled: selectedValues.includes(option.value),
  }));

export enum CostCenterCategoryNames {
  INTERNAL = 'INTERNAL',
  FAVORITES = 'FAVORITES',
  OTHERS = 'OTHERS',
}

export const COST_CENTER_CATEGORIES: EAutocompleteCategoryDefinitionProps[] = [
  {
    name: CostCenterCategoryNames.INTERNAL,
    mainLabel: i18n.t('timeTracking:costCenterDropdown.categories.internal'),
  },
  {
    name: CostCenterCategoryNames.FAVORITES,
    mainLabel: i18n.t('timeTracking:costCenterDropdown.categories.favorites'),
  },
  {
    name: CostCenterCategoryNames.OTHERS,
    mainLabel: i18n.t('timeTracking:costCenterDropdown.categories.others'),
  },
];

export const CONTACT_CATEGORIES = [
  {
    name: 'FAVORITES',
    mainLabel: i18n.t('casus:contactDropdown.categories.favorites'),
  },
  {
    name: 'OTHERS',
    mainLabel: i18n.t('casus:contactDropdown.categories.others'),
  },
];

const getTitleText = (involvements: InvolvementWithLabeledRoles[]): string => {
  const parts: string[] = [];

  involvements.forEach((inv) => {
    if (inv.type === 'CLIENT') {
      parts.push(i18n.t('casus:contactDropdown.roles.client'));
    } else if (inv.type === 'INVOICE_RECIPIENT') {
      parts.push(i18n.t('casus:contactDropdown.roles.invoiceRecipient'));
    } else {
      parts.push(...inv.involvedPartyRoles.map((it) => it.label));
    }
  });

  return parts.join(', ');
};

export const mapContactToOption = (
  contact: ContactWithInvolvements
): EAutocompleteOption => ({
  value: contact.id,
  displayText: contact.displayName,
  titleText: getTitleText(contact.involvements),
  categoryName: contact.category,
  Icon: getContactTypeIcon(contact.contactType),
});

export const mapContactEmailAddressToOption = (
  emailAddress: ContactEmailAddressWithInvolvements
): EAutocompleteOption => ({
  value: emailAddress.id,
  displayText: emailAddress.displayName,
  titleText: getTitleText(emailAddress.involvements),
  categoryName: emailAddress.category,
});
